import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuthActions, useAuthUser } from "../lib/auth";
import { apiFetch } from "../lib/api";
import { simpleMarkdownToHtml } from "../lib/utils";
import type { UserRole } from "../lib/types";

type Props = {
  /** Modal heading text */
  title?: string;
  /** Instructional subtext */
  subtitle?: string;
  /** If provided, only show the modal for users with these roles */
  roles?: UserRole[];
};

/**
 * Full-screen modal for users who haven't acknowledged
 * the current data use / privacy policy. Non-dismissible.
 */
export function DataUseAcknowledgmentModal({
  title = "Data Privacy Notice",
  subtitle = "Please review and acknowledge the following privacy notice to continue.",
  roles,
}: Props) {
  const { user, dataUseAcknowledgment } = useAuthUser();
  const { refreshMe } = useAuthActions();
  const [acknowledging, setAcknowledging] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Auto-focus modal on mount
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  // Focus trap — cycle Tab within the modal (no Escape, non-dismissible)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Tab" && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  if (!user) return null;
  if (roles && !roles.includes(user.role)) return null;
  if (user.data_use_acknowledged) return null;
  if (!dataUseAcknowledgment?.text) return null;

  const policyHtml = useMemo(() => simpleMarkdownToHtml(dataUseAcknowledgment.text), [dataUseAcknowledgment.text]);

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      await apiFetch("/api/v1/me/acknowledge_data_use", { method: "PATCH" });
      await refreshMe();
    } catch {
      // Will retry on next click
    } finally {
      setAcknowledging(false);
    }
  };

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[70] bg-black/60" />

      {/* Modal */}
      <div className="fixed inset-0 z-[71] flex items-end sm:items-center justify-center p-4">
        <div
          ref={modalRef}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="data-use-ack-title"
          tabIndex={-1}
          className="w-full max-w-lg bg-surface rounded-2xl shadow-2xl p-6 outline-none"
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-4 text-accent">
            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <h2 id="data-use-ack-title" className="text-lg font-semibold text-foreground">
              {title}
            </h2>
          </div>

          <p className="text-sm text-muted mb-4">
            {subtitle}
          </p>

          {/* Policy text */}
          <div className="bg-warning-bg border border-border rounded-lg p-4 mb-5 max-h-[40vh] overflow-y-auto">
            <div
              className="text-sm text-warning-text leading-relaxed prose-policy"
              dangerouslySetInnerHTML={{ __html: policyHtml }}
            />
          </div>

          {/* Action */}
          <button
            onClick={handleAcknowledge}
            disabled={acknowledging}
            className="w-full py-3 text-sm font-semibold text-accent-foreground bg-accent rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {acknowledging ? "Saving..." : "I Acknowledge"}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}
